import React, { useState, useEffect } from 'react';
import { Plus, ArrowRight, Edit, Trash2, Loader2 } from 'lucide-react';
import { PageHeader } from '@/components/shared/PageHeader';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { toast } from 'sonner';
import {
  createRedirect,
  getAllRedirects,
  deleteRedirect,
  updateRedirect,
} from '@/services/redirectApis';

export const RedirectsPage = () => {
  const [redirects, setRedirects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [isAddingNew, setIsAddingNew] = useState(false);
  const [selectedRedirect, setSelectedRedirect] = useState(null);
  const [deleteItem, setDeleteItem] = useState(null);

  // Form state
  const [fromUrl, setFromUrl] = useState('');
  const [toUrl, setToUrl] = useState('');
  const [redirectType, setRedirectType] = useState('301');

  // ==================== FETCH ====================
  const fetchRedirects = async () => {
    try {
      setLoading(true);
      const data = await getAllRedirects();
      setRedirects(Array.isArray(data) ? data : data?.data || []);
    } catch (error) {
      console.error(error);
      toast.error('Failed to load redirects');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRedirects();
  }, []);

  // ==================== FORM HELPERS ====================
  const resetForm = () => {
    setFromUrl('');
    setToUrl('');
    setRedirectType('301');
  };

  const openCreate = () => {
    resetForm();
    setIsAddingNew(true);
    setSelectedRedirect(null);
  };

  const openEdit = (item) => {
    setSelectedRedirect(item);
    setIsAddingNew(false);
    setFromUrl(item.from_url || item.from || '');
    setToUrl(item.to_url || item.to || '');
    setRedirectType(item.redirect_type || item.type || '301');
  };

  const closeSheet = () => {
    setIsAddingNew(false);
    setSelectedRedirect(null);
    resetForm();
  };

  // ==================== BUILD PAYLOAD ====================
  const buildPayload = (includeId = false) => {
  const payload = {
    from_url: fromUrl,
    to_url: toUrl,
    redirect_type: redirectType,
  };

  if (includeId && selectedRedirect?.id) {
    payload.id = selectedRedirect.id;
  }

  return payload;
};

const handleSave = async () => {
  if (!fromUrl.trim()) {
    toast.error('From URL is required');
    return;
  }
  if (!toUrl.trim()) {
    toast.error('To URL is required');
    return;
  }

  setSaving(true);

  try {
    if (isAddingNew) {
      await createRedirect(buildPayload(false));
      toast.success('Redirect created successfully');
    } else {
      // { id, from_url, to_url, redirect_type }
      await updateRedirect(buildPayload(true));
      toast.success('Redirect updated successfully');
    }

    closeSheet();
    fetchRedirects();
  } catch (error) {
    console.error(error);
    toast.error(error?.response?.data?.message || 'Failed to save redirect');
  } finally {
    setSaving(false);
  }
};

  // ==================== DELETE ====================
  const handleDelete = async () => {
    if (!deleteItem) return;

    try {
      await deleteRedirect(deleteItem.id);
      toast.success('Redirect deleted');
      setDeleteItem(null);
      fetchRedirects();
    } catch (error) {
      console.error(error);
      toast.error('Failed to delete redirect');
    }
  };

  return (
    <div>
      <PageHeader
        title="Redirects"
        description="Manage URL redirects and route mappings"
        breadcrumbs={['Admin', 'System', 'Redirects']}
        actions={
          <Button onClick={openCreate}>
            <Plus className="h-4 w-4 mr-2" />
            Add Redirect
          </Button>
        }
      />

      <Card className="rounded-xl border-slate-200 shadow-sm">
        <CardContent className="p-6">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
            </div>
          ) : (
            <div className="rounded-lg border border-slate-200 overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                      From URL
                    </TableHead>
                    <TableHead className="w-12"></TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                      To URL
                    </TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                      Type
                    </TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500 text-right">
                      Hits
                    </TableHead>
                    <TableHead className="w-12"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {redirects.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={6}
                        className="text-center py-12 text-slate-500"
                      >
                        No redirects found
                      </TableCell>
                    </TableRow>
                  ) : (
                    redirects.map((redirect) => (
                      <TableRow key={redirect.id} className="hover:bg-slate-50">
                        <TableCell>
                          <code className="text-sm bg-slate-100 px-2 py-1 rounded">
                            {redirect.from_url || redirect.from}
                          </code>
                        </TableCell>
                        <TableCell>
                          <ArrowRight className="h-4 w-4 text-slate-400" />
                        </TableCell>
                        <TableCell>
                          <code className="text-sm bg-slate-100 px-2 py-1 rounded">
                            {redirect.to_url || redirect.to}
                          </code>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">
                            {redirect.redirect_type || redirect.type}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <span className="text-sm font-medium text-slate-900">
                            {(redirect.hits ?? 0).toLocaleString()}
                          </span>
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => openEdit(redirect)}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-red-600"
                              onClick={() => setDeleteItem(redirect)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add / Edit Sheet */}
      <Sheet
        open={isAddingNew || !!selectedRedirect}
        onOpenChange={(open) => {
          if (!open) closeSheet();
        }}
      >
        <SheetContent>
          <SheetHeader>
            <SheetTitle>
              {isAddingNew ? 'Add Redirect' : 'Edit Redirect'}
            </SheetTitle>
          </SheetHeader>
          <div className="mt-6 space-y-6">
            <div>
              <Label>From URL</Label>
              <Input
                placeholder="/old-url"
                className="mt-2"
                value={fromUrl}
                onChange={(e) => setFromUrl(e.target.value)}
              />
            </div>
            <div>
              <Label>To URL</Label>
              <Input
                placeholder="/new-url"
                className="mt-2"
                value={toUrl}
                onChange={(e) => setToUrl(e.target.value)}
              />
            </div>
            <div>
              <Label>Redirect Type</Label>
              <RadioGroup
                value={redirectType}
                onValueChange={setRedirectType}
                className="mt-2"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="301" id="301" />
                  <Label htmlFor="301">301 (Permanent)</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="302" id="302" />
                  <Label htmlFor="302">302 (Temporary)</Label>
                </div>
              </RadioGroup>
            </div>
            <Button className="w-full" onClick={handleSave} disabled={saving}>
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isAddingNew ? 'Create Redirect' : 'Update Redirect'}
            </Button>
          </div>
        </SheetContent>
      </Sheet>

      {/* Delete Confirmation */}
      <ConfirmDialog
        open={!!deleteItem}
        onOpenChange={() => setDeleteItem(null)}
        title="Delete Redirect"
        description={`Are you sure you want to delete the redirect from "${deleteItem?.from_url || deleteItem?.from}"? This action cannot be undone.`}
        confirmLabel="Delete"
        variant="destructive"
        onConfirm={handleDelete}
      />
    </div>
  );
};